import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const AuthCallback = ({ onLogin }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Processing login...');
  const [error, setError] = useState(null);
  // Use a ref to prevent the StrictMode double-invoke from triggering the exchange twice
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        // Emergent Auth returns the session_id in the URL HASH FRAGMENT
        // (e.g. /auth/callback#session_id=XYZ), NOT in the query string.
        // Parse the hash with URLSearchParams.
        const rawHash = (window.location.hash || '').replace(/^#/, '');
        const hashParams = new URLSearchParams(rawHash);
        let sessionId = hashParams.get('session_id');
        // Defensive fallback: also accept a query param in case the flow ever changes
        if (!sessionId) {
          sessionId = new URLSearchParams(window.location.search).get('session_id');
        }

        if (!sessionId) {
          setError('No session ID provided');
          setStatus('Login failed');
          toast.error('Authentication failed - no session ID');
          setTimeout(() => navigate('/auth'), 2000);
          return;
        }

        // Strip the session_id from the URL so a refresh doesn't re-attempt the exchange
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch (_) { /* ignore */ }

        setStatus('Verifying your account...');

        // Exchange session_id for user data and tokens
        const response = await axios.post(`${API}/auth/google/session`, {
          session_id: sessionId
        });

        const { user, access_token, session_token } = response.data;

        // Store tokens and user info
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('session_token', session_token);
        localStorage.setItem('user_id', user.id);
        localStorage.setItem('user_email', user.email);
        localStorage.setItem('user_name', user.fullName || user.name);
        localStorage.setItem('user_picture', user.picture || '');
        localStorage.setItem('app_user_mode', user.userMode);
        localStorage.setItem('auth_provider', 'google');

        setStatus('Login successful! Redirecting...');
        toast.success(`Welcome, ${user.fullName || user.name}!`);

        // Call onLogin callback
        if (onLogin) {
          onLogin(user);
        }

        // Navigate based on user mode
        setTimeout(() => {
          if (user.userMode === 'Influencer') {
            navigate('/influencer-portal');
          } else if (user.userMode === 'Contractor') {
            navigate('/contractor/dashboard');
          } else {
            navigate('/dashboard');
          }
        }, 500);

      } catch (err) {
        console.error('Auth callback error:', err);
        const errorMsg = err.response?.data?.detail || 'Authentication failed';
        setError(errorMsg);
        setStatus('Login failed');
        toast.error(errorMsg);
        setTimeout(() => navigate('/auth'), 3000);
      }
    };

    handleCallback();
  }, [navigate, onLogin]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        {/* Google Logo Animation */}
        <div className="w-16 h-16 mx-auto mb-6 relative">
          {!error ? (
            <div className="animate-pulse">
              <svg width="64" height="64" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </div>
          ) : (
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <i className="fas fa-exclamation-triangle text-2xl text-red-500"></i>
            </div>
          )}
        </div>

        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          {status}
        </h1>
        
        {error && (
          <p className="text-red-500 text-sm mb-4">{error}</p>
        )}

        {!error && (
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        )}

        <p className="text-sm text-gray-400 mt-6">
          You will be redirected automatically
        </p>
      </div>
    </main>
  );
};

export default AuthCallback;
